import type { TicketEarningsEvent } from '@pulsify/shared/dto'
import * as m from '~/paraglide/messages'

const TIME_ZONE = 'Europe/Vienna'

type Payout = TicketEarningsEvent['payout']

export type PayoutTone = 'none' | 'planned' | 'done' | 'alert'

export function formatMoney(cents: number, currency: string, locale: string): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(cents / 100)
}

export function formatPayoutDay(iso: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    timeZone: TIME_ZONE,
    day: 'numeric',
    month: 'short',
  }).format(new Date(iso))
}

export function payoutState(
  payout: Payout,
  now: Date,
  locale: string,
): { label: string; tone: PayoutTone } {
  switch (payout.status) {
    case 'paid':
      return {
        label: payout.date
          ? m.earningsPaidOn({ date: formatPayoutDay(payout.date, locale) })
          : m.earningsPaid(),
        tone: 'done',
      }
    case 'waiting':
    case 'due':
      return {
        label:
          payout.date && new Date(payout.date) > now
            ? m.earningsPayoutOn({ date: formatPayoutDay(payout.date, locale) })
            : m.earningsPayoutRunning(),
        tone: 'planned',
      }
    case 'failed':
      return { label: m.earningsPayoutFailed(), tone: 'alert' }
    case 'review':
      return { label: m.earningsPayoutReview(), tone: 'planned' }
    case 'none':
      return { label: m.earningsPayoutNone(), tone: 'none' }
  }
}

export function nextPayoutDate(
  rows: readonly Pick<TicketEarningsEvent, 'payout'>[],
  now: Date,
): string | null {
  const planned = rows
    .map((row) => row.payout)
    .filter(
      (payout): payout is Payout & { date: string } =>
        (payout.status === 'waiting' || payout.status === 'due') &&
        payout.date !== null &&
        new Date(payout.date) > now,
    )
    .map((payout) => payout.date)
    .sort()
  return planned[0] ?? null
}
